<template>
  <div>
    <button
      type="button"
      @click.stop="expanded = !expanded"
      class="w-full flex items-center justify-between px-4 py-2 text-sm text-fg-secondary dark:text-fg-secondary-dark transition-colors"
      :aria-expanded="expanded"
    >
      <span class="flex items-center">
        <EyeIcon class="h-4 w-4 mr-3 text-fg-faint dark:text-fg-faint-dark flex-shrink-0" />
        Demo Mode
        <span
          v-if="demoMode.active.value"
          class="ml-2 px-1.5 py-0.5 rounded-full bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400 text-[10px] font-semibold leading-none"
        >On</span>
      </span>
      <ChevronDownIcon :class="['h-4 w-4 text-fg-faint dark:text-fg-faint-dark transition-transform flex-shrink-0', expanded ? 'rotate-180' : '']" />
    </button>

    <div v-if="expanded" class="px-4 pb-3 pt-2 space-y-3 bg-surface-muted dark:bg-surface-muted-dark" @click.stop>
      <div>
        <label class="block text-xs font-medium text-fg-secondary dark:text-fg-secondary-dark mb-1">Preview as</label>
        <select
          ref="teacherSelectRef"
          v-model="selectedTeacherId"
          @change="lastChangeAt = Date.now()"
          @keydown.enter.prevent="onSelectEnter"
          class="w-full px-2 py-1.5 rounded-md border border-stroke-strong dark:border-stroke-strong-dark bg-surface dark:bg-surface-dark text-fg-base dark:text-fg-base-dark text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option :value="null">Myself (admin)</option>
          <option v-for="t in teachers" :key="t.id" :value="t.id">{{ t.name }}</option>
        </select>
      </div>

      <label class="flex items-center gap-2 text-xs text-fg-secondary dark:text-fg-secondary-dark">
        <input v-model="scramble" type="checkbox" class="rounded border-stroke-strong dark:border-stroke-strong-dark" />
        Scramble names, emails &amp; phone numbers
      </label>

      <p v-if="error" class="text-xs text-red-600 dark:text-red-400">{{ error }}</p>

      <div class="flex items-center gap-2 pt-1">
        <button
          v-if="demoMode.active.value"
          type="button"
          @click="handleExit"
          class="flex-1 px-2 py-1.5 text-xs font-medium text-red-600 dark:text-red-400 hover:bg-surface dark:hover:bg-surface-dark rounded-md transition-colors border border-stroke-subtle dark:border-stroke-dark"
        >
          Exit
        </button>
        <button
          type="button"
          @click="handleStart"
          class="flex-1 px-2 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
        >
          {{ demoMode.active.value ? 'Update' : 'Start' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, nextTick, watch, onMounted } from 'vue'
import { EyeIcon, ChevronDownIcon } from '@heroicons/vue/24/outline'
import { useDemoMode } from '@/composables/useDemoMode'

const demoMode = useDemoMode()

const expanded = ref(false)
const teachers = ref([])
const selectedTeacherId = ref(demoMode.viewAsTeacher.value?.id ?? null)
const scramble = ref(demoMode.scrambleNames.value)
const error = ref('')
const teacherSelectRef = ref(null)

let teachersLoaded = Promise.resolve()

// "d" shortcut (AppRoot.vue): expand the panel and pop the Preview-as picker
// once the teacher list is in. showPicker() isn't in every browser — focusing
// still lets type-to-select and arrow keys work.
async function openPreviewPicker() {
  demoMode.previewPickerRequested.value = false
  expanded.value = true
  await teachersLoaded
  await nextTick()
  const el = teacherSelectRef.value
  if (!el) return
  el.focus()
  try { el.showPicker?.() } catch { /* needs user activation / unsupported */ }
}
watch(demoMode.previewPickerRequested, v => { if (v) openPreviewPicker() })

// Enter on the (closed) select starts the preview. The Enter that commits a
// choice in the open picker can also reach the select in some browsers, right
// after its change event — ignore that one so it takes a second Enter.
const lastChangeAt = ref(0)
function onSelectEnter() {
  if (Date.now() - lastChangeAt.value < 150) return
  handleStart()
}

onMounted(() => {
  teachersLoaded = (async () => {
    try {
      const res = await fetch('/api/admin/teachers?include_hidden=true')
      if (res.ok) teachers.value = await res.json()
    } catch {
      error.value = 'Could not load teacher list.'
    }
  })()
  if (demoMode.previewPickerRequested.value) openPreviewPicker()
})

function handleStart() {
  const teacher = teachers.value.find(t => t.id === selectedTeacherId.value) || null
  demoMode.enterDemoMode({
    teacher: teacher ? { id: teacher.id, name: teacher.name, permissions: teacher.permissions || [] } : null,
    scramble: scramble.value,
  })
  // Components fetch their data once on mount and don't watch demo-mode
  // state, so a reload is needed for the interceptor to actually take effect.
  window.location.reload()
}

function handleExit() {
  demoMode.exitDemoMode()
  window.location.reload()
}
</script>
